import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { confirmationPrompt } from './api-errors'
import { useLocationSelect } from './use-location'

const switchPortSchema = z.object({
  portId: z.string(),
  name: z.string().nullable(),
  type: z.string(),
  vlanTag: z.number().nullable(),
  enabled: z.boolean(),
  status: z.enum(['connected', 'disconnected', 'disabled']),
  clientMac: z.string().nullable(),
  clientIp: z.string().nullable(),
  clientDescription: z.string().nullable(),
  trafficReceived: z.string().nullable(),
  trafficSent: z.string().nullable(),
})

// GET /v1/switch-ports?storeCode= -> { code, success, data: SwitchPort[], switchVendor? }
const switchPortsEnvelopeSchema = z.object({
  success: z.boolean(),
  data: z.array(switchPortSchema).nullish(),
  switchVendor: z.enum(['MERAKI', 'EXTREME']).optional(),
})

export type SwitchPort = z.infer<typeof switchPortSchema>
export interface SwitchPortsData {
  ports: SwitchPort[]
  switchVendor: 'MERAKI' | 'EXTREME' | undefined
}

export const switchPortsQueryKey = (storeCode: string) => ['switch-ports', storeCode] as const

/** Switch ports for a store (uses the location's `aptosStoreCode` as `storeCode`). */
export function useSwitchPorts(idOrCode: string) {
  const api = useApi()
  const preview = usePreviewData()
  const storeCode = useLocationSelect(idOrCode, (loc) => loc?.aptosStoreCode ?? null).data ?? null
  return useQuery({
    queryKey: preview ? ['preview', 'switch-ports'] : switchPortsQueryKey(storeCode ?? idOrCode),
    queryFn: async (): Promise<SwitchPortsData> => {
      if (!storeCode) throw new Error('Store code not found')
      const res = await api.get(
        `/v1/switch-ports?storeCode=${encodeURIComponent(storeCode)}`,
        switchPortsEnvelopeSchema,
      )
      if (!res.success) throw new Error('Failed to fetch switch ports')
      return { ports: res.data ?? [], switchVendor: res.switchVendor }
    },
    enabled: !preview && storeCode !== null,
    ...(preview ? { initialData: preview.switchPorts, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

export type BounceKind = 'port' | 'poe'

/** Pure: endpoint for a bounce kind. */
export const bounceEndpoint = (kind: BounceKind): string =>
  kind === 'poe' ? '/v1/switch-ports/bounce-poe' : '/v1/switch-ports/bounce'

/** Pure: request body, with the confirmation answer only on the confirmed second call. */
export function bounceBody(storeCode: string, portId: string, confirmed: boolean) {
  return { storeCode, portId, ...(confirmed ? { confirmation: { answer: 'yes' as const } } : {}) }
}

export type BounceResult = { status: 'done' } | { status: 'needs-confirmation'; prompt: string }

const bounceEnvelopeSchema = z.object({ success: z.boolean() }).passthrough()

/**
 * Bounce a port or its PoE (POST /v1/switch-ports/bounce | bounce-poe). Two-step confirm protocol:
 * the first call (`confirmed` false/omitted) resolves `{status:'needs-confirmation', prompt}` when the API
 * answers HTTP 428; show the prompt, then `mutate({ ..., confirmed: true })`. Other errors throw `ApiError`.
 * `mutate({ storeCode, portId, kind, confirmed? })`.
 */
export function useBouncePort() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: {
      storeCode: string
      portId: string
      kind: BounceKind
      confirmed?: boolean
    }): Promise<BounceResult> => {
      if (preview) return { status: 'done' }
      try {
        await api.request(
          bounceEndpoint(v.kind),
          { method: 'POST', body: bounceBody(v.storeCode, v.portId, v.confirmed ?? false) },
          bounceEnvelopeSchema,
        )
        return { status: 'done' }
      } catch (error) {
        const prompt = confirmationPrompt(error)
        if (prompt !== null && !v.confirmed) return { status: 'needs-confirmation', prompt }
        throw error
      }
    },
    onSuccess: async (res, v) => {
      if (preview || res.status !== 'done') return
      await qc.invalidateQueries({ queryKey: switchPortsQueryKey(v.storeCode) })
    },
  })
}

/** Refresh button: refetch the port table (same GET). */
export function useRefreshSwitchPorts() {
  const qc = useQueryClient()
  const preview = usePreviewData()
  return () => (preview ? Promise.resolve() : qc.invalidateQueries({ queryKey: ['switch-ports'] }))
}
